<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import { computed, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import LeLogo from "@/components/LeLogo.vue";
import { useLoginRoute } from "@/composables";
import { config } from "@/config";
import { vMatomoEvent } from "@/directives";
import { useUserStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory, type UserType } from "@/types";

import LeHeaderBase from "./LeHeaderBase.vue";
import LeHeaderGreeting from "./LeHeaderGreeting.vue";
import LeHeaderPromotion from "./LeHeaderPromotion.vue";

const props = defineProps<{
  companyName: string;
  username: string;
  isAuthenticated: boolean;
  isNetwork: boolean;
  userType: UserType;
  isPaying: boolean;
  isEnterpriseAdmin: boolean;
  isEnterpriseDictionaryEditor: boolean;
  networkName: string;
}>();

const { t } = useI18n();
const router = useRouter();
const loginRoute = useLoginRoute();

const { language, isMobile } = config.app;

const showPlus = ref<boolean>(false);
const showAbcPromotion = ref<boolean>(false);
const userStore = useUserStore();

onBeforeMount(async () => {
  const userData = await userStore.getUser();

  showPlus.value = !isMobile && userData.featureFlags.showPlusLink;
  showAbcPromotion.value = !isMobile && userData.featureFlags.showAbcPromotion;
});

const showNavigationLinks = computed(() => !props.isAuthenticated);

const isThisPage = (href: string): boolean => document.location.pathname === href;
</script>

<template>
  <LeHeaderBase
    class="le-header"
    :logo-url="`/${language}/`"
    :nav-container-class="{
      'le-header__nav-container-plus': showPlus,
      'le-header__nav-container-abc': showAbcPromotion
    }"
  >
    <template #logo-background>
      <div class="le-header__logo-background d-none d-sm-block"></div>
    </template>

    <template #logo>
      <LeLogo role="presentation" class="le-header__logo" />
    </template>

    <template #nav>
      <LeHeaderPromotion
        v-if="showPlus"
        :text="t('plus.text')"
        :label="t('plus.label')"
        href="/plus?mtm_campaign=plus-link&mtm_kwd=navbar"
      />
      <LeHeaderPromotion
        v-else-if="showAbcPromotion"
        :text="t('abc.text')"
        :label="t('abc.label')"
        href="https://abc.ordbogen.com"
        class="d-none d-lg-flex"
      />
      <nav v-else-if="showNavigationLinks" class="le-header__nav">
        <ul>
          <li>
            <a
              v-matomo-event:click="{
                category: MatomoEventCategory.Header,
                action: MatomoEventAction.ClickHeaderButton,
                label: `Clicked 'our dictionaries'`
              }"
              :href="`/${language}/dictionaries/`"
              :class="[
                'le-header__nav-link',
                { 'le-header__nav-link--active': isThisPage(`/${language}/dictionaries/`) }
              ]"
            >
              {{ t("navigation-links.dictionaries") }}
            </a>
          </li>
          <li>
            <a
              v-matomo-event:click="{
                category: MatomoEventCategory.Header,
                action: MatomoEventAction.ClickHeaderButton,
                label: `Clicked 'help'`
              }"
              :href="`/${language}/support/`"
              :class="['le-header__nav-link', { 'le-header__nav-link--active': isThisPage(`/${language}/support/`) }]"
            >
              {{ t("navigation-links.support") }}
            </a>
          </li>
        </ul>
      </nav>
    </template>

    <template #buttons>
      <template v-if="props.isAuthenticated">
        <LeHeaderGreeting
          v-if="props.isAuthenticated"
          :company-name="props.companyName"
          :is-network="props.isNetwork"
          :username="props.username"
          :user-type="props.userType"
          :is-paying="props.isPaying"
          :is-enterprise-admin="props.isEnterpriseAdmin"
          :is-enterprise-dictionary-editor="props.isEnterpriseDictionaryEditor"
          :network-name="props.networkName"
        />
      </template>
      <template v-else>
        <LeButton
          v-matomo-event:click="{
            category: MatomoEventCategory.Header,
            action: MatomoEventAction.ClickHeaderButton,
            label: `Clicked 'Get access'`
          }"
          tag="a"
          :href="router.resolve({ name: 'subscription' }).href"
          color="neutral-dark-background"
          size="small"
          variant="outline"
          class="d-none d-md-block"
        >
          {{ t("get-access") }}
        </LeButton>
        <LeButton
          v-matomo-event:click="{
            category: MatomoEventCategory.Header,
            action: MatomoEventAction.ClickHeaderButton,
            label: `Clicked 'Log in'`
          }"
          tag="a"
          :href="loginRoute.href"
          color="neutral-dark-background"
        >
          {{ t("log-in") }}
        </LeButton>
      </template>
    </template>
  </LeHeaderBase>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-header {
  :deep(&__nav-container-plus) {
    grid-template-columns: 1fr auto 2.2fr;
  }

  :deep(&__nav-container-abc) {
    #media-breakpoint-up(lg, {
      grid-template-columns: 1fr auto 2.2fr;
    });
  }

  &__logo-background {
    position: absolute;
    width: 400px;
    height: 100%;
    margin-left: -100px;
    background: linear-gradient(90deg, @colors[@primary][700], @colors[@primary][500], @colors[@primary][700]);
  }

  &__logo {
    width: 191px;
    height: 34px;
    color: white;
  }

  &__nav {
    display: none;
    align-items: center;

    #media-breakpoint-up(lg, {
      display: flex;
    });

    ul {
      display: flex;
      gap: 30px;
      margin: 0;
      padding-inline-start: 0;

      li {
        display: flex;
        align-items: center;
        line-height: normal;
        list-style: none;
      }
    }

    &-link {
      color: @colors[@neutrals][100];

      &:hover,
      &.focus-visible {
        text-decoration-line: underline;
      }

      &--active {
        font-weight: 600;
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "da": {
    "plus": {
      "text": "Prøv Ordbogen Plus",
      "label": "NY"
    },
    "abc": {
      "text": "Du har også adgang til ABC-Ordbogen",
      "label": "TIP"
    },
    "navigation-links": {
      "dictionaries": "Vores ordbøger",
      "support": "Hjælp"
    },
    "get-access": "Få adgang",
    "log-in": "Log ind"
  },
  "en": {
    "plus": {
      "text": "Try Ordbogen Plus",
      "label": "NEW"
    },
    "navigation-links": {
      "dictionaries": "Our dictionaries",
      "support": "Help"
    },
    "get-access": "Get access",
    "log-in": "Log in"
  },
  "de": {
    "plus": {
      "text": "Testen Sie Ordbogen Plus",
      "label": "NEU"
    },
    "navigation-links": {
      "dictionaries": "Unsere Wörterbücher",
      "support": "Hilfe"
    },
    "get-access": "Zugriff erhalten",
    "log-in": "Anmelden"
  }
}
</i18n>
